import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ShoppingOutlined,
  BulbOutlined,
  CalendarOutlined,
  FunnelPlotOutlined,
  PartitionOutlined,
  CloseOutlined,
  CrownOutlined,
  SettingOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { usePermissions, SCOPES, ACTIONS } from '../../contexts/PermissionsContext';
import { useAuth } from '../../contexts/AuthContext';

interface MoreMenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  isAdmin?: boolean;
}

interface MoreMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const MoreMenu: React.FC<MoreMenuProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const { isPlatformAdmin } = useAuth();

  const menuItems: MoreMenuItem[] = useMemo(() => {
    const items: MoreMenuItem[] = [];

    // Appuntamenti (visibile a tutti)
    items.push({
      key: '/appointments',
      icon: <CalendarOutlined />,
      label: 'Appuntamenti',
    });

    // Pipeline Opportunita (visibile a tutti)
    items.push({
      key: '/opportunities',
      icon: <FunnelPlotOutlined />,
      label: 'Pipeline',
    });

    // Prodotti
    if (hasPermission(SCOPES.PRODUCTS, ACTIONS.VIEW)) {
      items.push({
        key: '/products',
        icon: <ShoppingOutlined />,
        label: t('nav.products'),
      });
    }

    // Gruppi Opzioni
    if (hasPermission(SCOPES.BUNDLES, ACTIONS.VIEW)) {
      items.push({
        key: '/option-groups',
        icon: <BulbOutlined />,
        label: 'Gruppi Opzioni',
      });
    }

    // Organigramma - visibile a tutti
    items.push({
      key: '/users/hierarchy',
      icon: <PartitionOutlined />,
      label: 'Organigramma',
    });

    // Impostazioni
    if (hasPermission(SCOPES.SETTINGS, ACTIONS.VIEW)) {
      items.push({
        key: '/settings',
        icon: <SettingOutlined />,
        label: 'Impostazioni',
      });
    }

    // Platform Admin - Gestione Piattaforma
    if (isPlatformAdmin) {
      items.push({
        key: '/admin/tenants',
        icon: <CrownOutlined />,
        label: 'Gestione Piattaforma',
      });
      items.push({
        key: '/admin/ai',
        icon: <RobotOutlined />,
        label: 'Gestione AI',
      });
    }

    return items;
  }, [hasPermission, t, isPlatformAdmin]);

  const handleNavigate = (key: string) => {
    navigate(key);
    onClose();
  };

  const isActive = (key: string) => {
    return location.pathname === key || location.pathname.startsWith(key + '/');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="more-menu__overlay" onClick={onClose} />

      {/* Bottom Sheet */}
      <div className="more-menu">
        {/* Handle bar */}
        <div className="more-menu__handle">
          <div className="more-menu__handle-bar" />
        </div>

        {/* Header */}
        <div className="more-menu__header">
          <h3 className="more-menu__title">Altre sezioni</h3>
          <button className="more-menu__close" onClick={onClose}>
            <CloseOutlined />
          </button>
        </div>

        {/* Menu Items */}
        <div className="more-menu__items">
          {menuItems.map((item) => (
            <button
              key={item.key}
              className={`more-menu__item ${isActive(item.key) ? 'more-menu__item--active' : ''}`}
              onClick={() => handleNavigate(item.key)}
            >
              <span className="more-menu__item-icon">{item.icon}</span>
              <span className="more-menu__item-label">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default MoreMenu;
